<template>
  <div class="permissions-chart">
    <!-- Empty state -->
    <div v-if="!hasAnyPermission" class="empty-state">
      <i class="material-icons empty-icon">shield</i>
      <p class="empty-text">No admin permissions found for this location.</p>
    </div>

    <!-- Tier flow -->
    <div v-else class="tier-flow">
      <div
        v-for="(tier, idx) in tiers"
        :key="tier.key"
        class="tier-wrapper"
      >
        <!-- Connector line (between tiers) -->
        <div v-if="idx > 0" class="connector" :class="{ active: tier.held }">
          <div class="connector-line" />
          <i class="material-icons connector-arrow">arrow_downward</i>
        </div>

        <!-- Tier card -->
        <div class="tier-card" :class="{ held: tier.direct, inherited: tier.inherited, muted: !tier.held }">
          <div class="tier-header">
            <div class="tier-icon-wrap" :class="tier.held ? `bg-${tier.color}` : 'bg-muted'">
              <i class="material-icons">{{ tier.icon }}</i>
            </div>
            <div class="tier-label-wrap">
              <span class="tier-label">{{ tier.label }}</span>
              <span v-if="tier.direct" class="tier-badge" :class="`badge-${tier.color}`">Active</span>
              <span v-else-if="tier.inherited" class="tier-badge badge-inherited">Inherited</span>
              <span v-if="tier.inherited && tier.inheritedFrom" class="tier-inherited-source">Via {{ tier.inheritedFrom }} admin</span>
            </div>
          </div>

          <!-- Description (held tiers only) -->
          <p v-if="tier.held" class="tier-description">{{ tier.description }}</p>

          <!-- Inherits-all message -->
          <p v-if="tier.held && tier.inheritsAll" class="tier-inherits-all">
            All {{ inheritsAllLabel(tier.key) }} in this location
          </p>

          <!-- Entity list (held tiers only) -->
          <div v-if="tier.held && tier.entities.length" class="tier-entities">
            <template v-for="(group, gIdx) in tier.entities" :key="gIdx">
              <!-- Grouped entities (workgroup/instance tiers) -->
              <div v-if="group.children" class="entity-group">
                <div class="entity-group-label">
                  <i class="material-icons entity-group-icon">folder_open</i>
                  {{ group.label }}
                </div>
                <div class="entity-chips">
                  <span
                    v-for="(child, cIdx) in group.children"
                    :key="cIdx"
                    class="entity-chip"
                    :class="`chip-${tier.color}`"
                  >{{ child.label }}<span v-if="child.inheritedFrom" class="entity-inherited-tag">&middot; via {{ child.inheritedFrom }}</span></span>
                </div>
              </div>
              <!-- Flat entities (org/location/activity tiers) -->
              <span
                v-else
                class="entity-chip"
                :class="`chip-${tier.color}`"
              >{{ group.label }}</span>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  permissions: {
    type: Object,
    default: () => ({}),
  },
});

/* ── Super Admin tier definition (conditionally prepended) ── */
const SUPER_ADMIN_META = {
  key: 'super_admin',
  flag: 'is_super_admin',
  details: null,
  label: 'Super Admin',
  icon: 'admin_panel_settings',
  color: 'amber',
  description: 'Has full super administrator access across the entire platform.',
};

/* ── Helpers for inherited-permission fields ── */
function isTierInherited(meta) {
  return typeof props.permissions?.[`${meta.key}_admin_inherited_from`] === 'string';
}

function inheritedFrom(meta) {
  return props.permissions?.[`${meta.key}_admin_inherited_from`] || null;
}

function inheritsAll(meta) {
  return !!props.permissions?.[`${meta.key}_admin_inherits_all`];
}

function inheritsAllLabel(key) {
  const labels = {
    activity: 'activities',
    workgroup: 'workgroups',
    instance: 'event instances',
    instance_workgroup: 'event slots',
  };
  return labels[key] || 'items';
}

const TIER_META = [
  {
    key: 'organization',
    flag: 'is_organization_admin',
    details: 'organization_admin_details',
    label: 'Organization Admin',
    icon: 'corporate_fare',
    color: 'indigo',
    description: 'Can manage all locations, settings, billing, and users within the organization.',
  },
  {
    key: 'location',
    flag: 'is_location_admin',
    details: 'location_admin_details',
    label: 'Location Admin',
    icon: 'location_on',
    color: 'blue',
    description: 'Can manage all activities, workgroups, schedules, users, and settings within this location.',
  },
  {
    key: 'activity',
    flag: 'is_activity_admin',
    details: 'activity_admin_details',
    label: 'Activity Admin',
    icon: 'event',
    color: 'teal',
    description: 'Can manage schedules, workgroups, and users for the listed activities.',
  },
  {
    key: 'workgroup',
    flag: 'is_workgroup_admin',
    details: 'workgroup_admin_details',
    label: 'Workgroup Admin',
    icon: 'groups',
    color: 'purple',
    description: 'Can manage schedules and users for the listed workgroups.',
  },
  {
    key: 'instance',
    flag: 'is_instance_admin',
    details: 'instance_admin_details',
    label: 'Instance Admin',
    icon: 'calendar_today',
    color: 'orange',
    description: 'Can manage schedules and users for the listed specific event instances.',
  },
  {
    key: 'instance_workgroup',
    flag: 'is_instance_workgroup_admin',
    details: 'instance_workgroup_admin_details',
    label: 'Instance Workgroup Admin',
    icon: 'group_work',
    color: 'rose',
    description: 'Can manage users scheduled in the listed specific event/workgroup slots.',
  },
];

function buildEntities(meta, detailItems) {
  if (!detailItems?.length) return [];

  switch (meta.key) {
    case 'organization':
      return detailItems.map(d => ({ label: d.organization_name || d.name || 'Organization' }));
    case 'location':
      return detailItems.map(d => ({ label: d.location_name || d.name || 'Location' }));
    case 'activity':
      return detailItems.map(d => ({ label: d.activity_name || d.name }));
    case 'workgroup': {
      // Group workgroups by activity
      const grouped = {};
      for (const d of detailItems) {
        const actName = d.activity_name || 'General';
        if (!grouped[actName]) grouped[actName] = [];
        grouped[actName].push({ label: d.workgroup_name || d.name, inheritedFrom: d.inherited_from || null });
      }
      return Object.entries(grouped).map(([act, wgs]) => ({
        label: act,
        children: wgs,
      }));
    }
    case 'instance': {
      // Group instances by activity
      const grouped = {};
      for (const d of detailItems) {
        const actName = d.activity_name || 'General';
        if (!grouped[actName]) grouped[actName] = [];
        const dateStr = d.instance_date || d.start_date || d.name || 'Instance';
        grouped[actName].push({ label: formatDate(dateStr), inheritedFrom: d.inherited_from || null });
      }
      return Object.entries(grouped).map(([act, dates]) => ({
        label: act,
        children: dates,
      }));
    }
    case 'instance_workgroup': {
      // Group by activity → instance date → workgroup name
      const grouped = {};
      for (const d of detailItems) {
        const actName = d.activity_name || 'General';
        const instDate = formatDate(d.instance_date || d.start_date || '');
        const key = `${actName} - ${instDate}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push({ label: d.instance_workgroup_name || d.workgroup_name || d.name || 'Workgroup', inheritedFrom: d.inherited_from || null });
      }
      return Object.entries(grouped).map(([key, wgs]) => ({
        label: key,
        children: wgs,
      }));
    }
    default:
      return detailItems.map(d => ({ label: d.name || JSON.stringify(d) }));
  }
}

function formatDate(str) {
  if (!str) return 'N/A';
  const d = new Date(str);
  if (isNaN(d.getTime())) return str; // Return as-is if not parseable
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Build a tier-keyed map from the server-provided permission_descriptions
const descriptionMap = computed(() => {
  const entries = props.permissions?.permission_descriptions || [];
  const map = {};
  for (const entry of entries) {
    if (entry.tier) map[entry.tier] = entry;
  }
  return map;
});

const tiers = computed(() => {
  const result = [];
  const descMap = descriptionMap.value;

  // Super Admin tier — only when is_super_admin === true
  if (props.permissions?.is_super_admin === true) {
    const serverDesc = descMap['super_admin'];
    result.push({
      ...SUPER_ADMIN_META,
      held: true,
      direct: true,
      inherited: false,
      inheritedFrom: null,
      inheritsAll: false,
      entities: [],
      description: serverDesc?.description || SUPER_ADMIN_META.description,
    });
  }

  for (const meta of TIER_META) {
    const held = !!props.permissions?.[meta.flag];
    const inherited = held && isTierInherited(meta);
    const detailItems = props.permissions?.[meta.details] || [];
    const serverDesc = descMap[meta.key];
    result.push({
      ...meta,
      held,
      direct: held && !inherited,
      inherited,
      inheritedFrom: inheritedFrom(meta),
      inheritsAll: inheritsAll(meta),
      entities: held ? buildEntities(meta, detailItems) : [],
      description: serverDesc?.description || meta.description,
    });
  }

  return result;
});

const hasAnyPermission = computed(() =>
  tiers.value.some(t => t.held)
);
</script>

<style scoped>
.permissions-chart {
  padding: 0.5rem 0;
}

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 1rem;
  color: #9e9e9e;
}
.empty-icon {
  font-size: 64px;
  margin-bottom: 1rem;
  opacity: 0.4;
}
.empty-text {
  font-size: 1rem;
  margin: 0;
}

/* ── Tier flow ── */
.tier-flow {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

/* ── Connector ── */
.connector {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 36px;
  position: relative;
  color: #ccc;
}
.connector.active {
  color: #667eea;
}
.connector-line {
  width: 2px;
  flex: 1;
  background: currentColor;
  opacity: 0.5;
}
.connector-arrow {
  font-size: 18px;
  margin-top: -4px;
  opacity: 0.5;
}

/* ── Tier card ── */
.tier-card {
  width: 100%;
  max-width: 560px;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  transition: all 0.25s ease;
}
.tier-card.held {
  background: #fff;
  border: 1.5px solid #e0e7ff;
  box-shadow: 0 2px 12px rgba(102, 126, 234, 0.1);
}
.tier-card.inherited {
  background: #f8f9ff;
  border: 1.5px solid #c5cae9;
  box-shadow: none;
}
.tier-card.muted {
  background: #fafafa;
  border: 1.5px dashed #e0e0e0;
  opacity: 0.6;
}

/* ── Tier header ── */
.tier-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.tier-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}
.tier-icon-wrap i { font-size: 22px; }

/* Icon backgrounds */
.bg-indigo  { background: linear-gradient(135deg, #667eea, #764ba2); }
.bg-blue    { background: linear-gradient(135deg, #2196f3, #1976d2); }
.bg-teal    { background: linear-gradient(135deg, #009688, #00796b); }
.bg-purple  { background: linear-gradient(135deg, #9c27b0, #7b1fa2); }
.bg-orange  { background: linear-gradient(135deg, #ff9800, #f57c00); }
.bg-rose    { background: linear-gradient(135deg, #e91e63, #c2185b); }
.bg-amber   { background: linear-gradient(135deg, #ffb300, #ff8f00); }
.bg-muted   { background: #ccc; }

.tier-label-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.tier-label {
  font-weight: 700;
  font-size: 0.95rem;
  color: #344767;
}
.muted .tier-label {
  color: #9e9e9e;
  font-weight: 600;
}

/* Badge */
.tier-badge {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #fff;
}
.badge-indigo     { background: #667eea; }
.badge-blue       { background: #2196f3; }
.badge-teal       { background: #009688; }
.badge-purple     { background: #9c27b0; }
.badge-orange     { background: #ff9800; }
.badge-rose       { background: #e91e63; }
.badge-amber      { background: #ffb300; }
.badge-inherited  { background: #e8eaf6; color: #5c6bc0; }

.tier-inherited-source {
  font-size: 0.72rem;
  color: #7986cb;
  font-weight: 500;
}

/* ── Description ── */
.tier-description {
  margin: 0.5rem 0 0 3.25rem; /* align with label text */
  font-size: 0.82rem;
  color: #6c757d;
  line-height: 1.45;
}

/* ── Entities ── */
.tier-entities {
  margin: 0.65rem 0 0 3.25rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.entity-group {
  width: 100%;
  margin-bottom: 0.35rem;
}
.entity-group-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #555;
  margin-bottom: 0.3rem;
}
.entity-group-icon {
  font-size: 16px;
  color: #999;
}
.entity-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  padding-left: 1.25rem;
}

.entity-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 16px;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.5;
}
.chip-indigo  { background: #e8eaf6; color: #3949ab; }
.chip-blue    { background: #e3f2fd; color: #1565c0; }
.chip-teal    { background: #e0f2f1; color: #00695c; }
.chip-purple  { background: #f3e5f5; color: #7b1fa2; }
.chip-orange  { background: #fff3e0; color: #ef6c00; }
.chip-rose    { background: #fce4ec; color: #c2185b; }
.chip-amber   { background: #fff8e1; color: #ff6f00; }

/* ── Inherits-all message ── */
.tier-inherits-all {
  margin: 0.5rem 0 0 3.25rem;
  font-size: 0.82rem;
  font-style: italic;
  color: #7986cb;
  line-height: 1.45;
}

/* ── Per-item inherited tag ── */
.entity-inherited-tag {
  font-size: 0.68rem;
  color: #9e9e9e;
  font-weight: 500;
  margin-left: 3px;
}
</style>
